// Clerk -> Firebase bridge: exchange the Clerk session for a Firebase custom
// token (via /api/firebase-token) and sign in, so Firestore sees a real
// request.auth.uid (== Clerk user id) with role/companyId claims. Idempotent;
// non-fatal while the Firestore rules are still open (Phase 5 makes it required).
import { signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';

// One sign-in attempt per page LOAD (module load resets this). We deliberately
// do NOT skip when auth.currentUser exists: a session persisted in IndexedDB from
// a previous load can be stale or for a different user, and skipping would leave
// the app running on it (→ permission-denied everywhere). signInWithCustomToken
// replaces whatever session exists with a fresh one for the current Clerk user.
let signInPromise: Promise<void> | null = null;

export function ensureFirebaseSignIn(getToken: () => Promise<string | null>): Promise<void> {
  if (signInPromise) return signInPromise;
  signInPromise = (async () => {
    try {
      const clerkToken = await getToken();
      if (!clerkToken) {
        // Clerk token not ready yet — allow a retry on the next call this load.
        signInPromise = null;
        return;
      }
      const resp = await fetch('/api/firebase-token', {
        method: 'POST',
        headers: { Authorization: `Bearer ${clerkToken}` },
      });
      if (!resp.ok) {
        console.warn('firebase-token request failed:', resp.status);
        return;
      }
      const { token } = await resp.json();
      const cred = await signInWithCustomToken(auth, token);
      console.log('✅ ensureFirebaseSignIn: signed into Firebase as', cred.user?.uid);
    } catch (e) {
      console.warn('ensureFirebaseSignIn failed (non-fatal):', e);
    }
  })();
  return signInPromise;
}

// Resolve once THIS load's sign-in has completed (not a possibly-stale persisted
// session), so callers that read the user's own doc (role, subscription, company)
// wait for the fresh Firebase identity. Falls open after timeoutMs so a failed or
// slow sign-in never blocks a read permanently.
export function waitForFirebaseAuth(timeoutMs = 8000): Promise<void> {
  if (signInPromise) {
    return Promise.race([
      signInPromise.catch(() => {}),
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  }
  if (auth.currentUser) return Promise.resolve();
  return new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) { unsub(); resolve(); }
    });
    setTimeout(() => { unsub(); resolve(); }, timeoutMs);
  });
}
